import { authService } from './auth-service';
import type { Org, OrgRole, OrgAclEntry, AccessRole, AccessId } from '@/types/org';

// Raw shapes returned by the API (matching OpenAPI spec JSON)
interface ApiOrgAddress {
  street1: string;
  street2?: string;
  city: string;
  state: string;
  postalCode: string;
}

interface ApiOrgPrefs {
  skipCheckoutConfirmations: boolean;
}

interface ApiOrgAcl {
  aclId?: string;
  email?: string;
  roles: string[];
  createdTS?: string;
}

interface ApiOrgAnonAccess {
  accessId?: string;
  token?: string;
  name?: string;
  roles?: string[];
  disabled?: boolean;
  createdTS?: string;
}

interface ApiOrg {
  id?: string;
  name: string;
  storeNumber?: string;
  address: ApiOrgAddress;
  prefs: ApiOrgPrefs;
  acl?: ApiOrgAcl[];
  accessIds?: ApiOrgAnonAccess[];
}

export interface OrgReq {
  name: string;
  storeNumber?: string;
  address: ApiOrgAddress;
  prefs: ApiOrgPrefs;
}

function mapApiAnonAccess(a: ApiOrgAnonAccess): AccessId {
  return {
    id: a.accessId ?? '',
    token: a.token,
    name: a.name,
    roles: (a.roles ?? []) as AccessRole[],
    disabled: a.disabled,
    createdTS: a.createdTS,
  };
}

function mapApiOrg(data: ApiOrg): Org {
  return {
    id: data.id ?? '',
    name: data.name,
    storeNumber: data.storeNumber,
    address: {
      street1: data.address.street1,
      street2: data.address.street2,
      city: data.address.city,
      state: data.address.state,
      postalCode: data.address.postalCode,
    },
    prefs: {
      skipCheckoutConfirmations: data.prefs.skipCheckoutConfirmations,
    },
    acl: (data.acl ?? []).map(a => ({
      aclId: a.aclId,
      email: a.email ?? '',
      roles: (a.roles ?? []) as OrgRole[],
    })),
    accessIds: (data.accessIds ?? []).map(mapApiAnonAccess),
  };
}

async function apiFetch(path: string, init: RequestInit): Promise<Response> {
  await authService.ensureFreshToken();
  const token = authService.getToken();
  return fetch(`/api/ac${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });
}

export interface AnonTokenInfo {
  orgId: string;
  orgName: string;
  roles: string[];
}

export interface OrgScanInfo {
  orgName: string;
  roles: string[];
}

async function getOrgScanInfo(orgId: string): Promise<OrgScanInfo> {
  const res = await apiFetch(`/v1/org/${encodeURIComponent(orgId)}/scan`, { method: 'GET' });
  if (res.status === 403) throw new Error('forbidden');
  if (res.status === 404) throw new Error('not_found');
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json();
}

async function getAnonToken(token: string): Promise<AnonTokenInfo> {
  const res = await fetch(`/api/ac/v1/public/token/${encodeURIComponent(token)}`);
  if (res.status === 401) throw new Error('disabled');
  if (res.status === 404) throw new Error('not_found');
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json();
}

async function getOrgs(): Promise<Org[]> {
  const res = await apiFetch('/v1/org', { method: 'GET' });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }
  const data: ApiOrg[] = await res.json();
  return data.map(mapApiOrg);
}

async function getOrgById(id: string): Promise<Org> {
  const res = await apiFetch(`/v1/org/${encodeURIComponent(id)}`, { method: 'GET' });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }
  return mapApiOrg(await res.json());
}

async function updateOrg(orgId: string, req: OrgReq): Promise<Org> {
  const res = await apiFetch(`/v1/org/${encodeURIComponent(orgId)}`, {
    method: 'PUT',
    body: JSON.stringify(req),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }
  return mapApiOrg(await res.json());
}

async function createOrg(req: OrgReq): Promise<Org> {
  const res = await apiFetch('/v1/org', {
    method: 'POST',
    body: JSON.stringify(req),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }
  return mapApiOrg(await res.json());
}

interface OrgAnonAccessReq {
  name?: string;
  roles?: string[];
  disabled?: boolean;
}

async function createOrgAnonAccess(orgId: string, req: OrgAnonAccessReq): Promise<AccessId> {
  const res = await apiFetch(`/v1/org/${encodeURIComponent(orgId)}/anonAccess`, {
    method: 'POST',
    body: JSON.stringify(req),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }
  return mapApiAnonAccess(await res.json());
}

async function updateOrgAnonAccess(orgId: string, accessId: string, req: OrgAnonAccessReq): Promise<AccessId> {
  const res = await apiFetch(`/v1/org/${encodeURIComponent(orgId)}/anonAccess/${encodeURIComponent(accessId)}`, {
    method: 'PUT',
    body: JSON.stringify(req),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }
  return mapApiAnonAccess(await res.json());
}

async function deleteOrgAnonAccess(orgId: string, accessId: string): Promise<void> {
  const res = await apiFetch(`/v1/org/${encodeURIComponent(orgId)}/anonAccess/${encodeURIComponent(accessId)}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }
}

async function createOrgAcl(orgId: string, email: string, roles: string[]): Promise<OrgAclEntry> {
  const res = await apiFetch(`/v1/org/${encodeURIComponent(orgId)}/acl`, {
    method: 'POST',
    body: JSON.stringify({ email, roles }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }
  const a: ApiOrgAcl = await res.json();
  return { aclId: a.aclId, email: a.email ?? '', roles: (a.roles ?? []) as OrgRole[] };
}

async function updateOrgAcl(orgId: string, aclId: string, roles: string[]): Promise<OrgAclEntry> {
  const res = await apiFetch(`/v1/org/${encodeURIComponent(orgId)}/acl/${encodeURIComponent(aclId)}`, {
    method: 'PUT',
    body: JSON.stringify({ roles }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }
  const a: ApiOrgAcl = await res.json();
  return { aclId: a.aclId, email: a.email ?? '', roles: (a.roles ?? []) as OrgRole[] };
}

async function deleteOrgAcl(orgId: string, aclId: string): Promise<void> {
  const res = await apiFetch(`/v1/org/${encodeURIComponent(orgId)}/acl/${encodeURIComponent(aclId)}`, {
    method: 'DELETE',
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }
}

async function getOrgEntitlements(orgId: string): Promise<string[]> {
  const res = await apiFetch(`/v1/org/${encodeURIComponent(orgId)}/entitlements`, { method: 'GET' });
  if (res.status === 403) throw new Error('forbidden');
  if (res.status === 404) throw new Error('not_found');
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(text || `HTTP ${res.status}`);
  }
  const data: { entitlements: string[] } = await res.json();
  return data.entitlements;
}

export const orgService = { getOrgScanInfo, getAnonToken, getOrgs, getOrgById, createOrg, updateOrg, createOrgAcl, updateOrgAcl, deleteOrgAcl, createOrgAnonAccess, updateOrgAnonAccess, deleteOrgAnonAccess, getOrgEntitlements };